/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useState, useRef } from 'react';
import { InputAdornment, TextField } from '@mui/material';
import { ClickAwayListener } from '@mui/base';
import { DateRange } from 'react-date-range';
import { STAY_DATE_END_DATE } from 'modules/common/constants/date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { addDays } from 'date-fns';

/**
 * Custom date range picker common component that uses across modules for select date range
 * @param {Function} handleChange - function for setting data range
 * @param {Array} ranges - start date and end data of the picker
 * @param {String} label - textfield label
 * @param {String} adornment - whether we need to adornment is beginning or not
 * @param {String} dateValue - selected date value
 * @param {String} cssClassName - use style for entire component
 * @param {String} maxDate - maximum date what we need to define
 * @returns
 */
const AlertDateRangePicker = (props) => {
  // destruct the props object
  const { handleChange, ranges, label, adornment, dateValue, cssClassName, disabled } = props;
  //
  const [open, setOpen] = useState(false);
  const refTarget = useRef(null);

  const dateRange =
    ranges !== null
      ? [
          {
            startDate: new Date(ranges[0].startDate),
            endDate: new Date(ranges[0].endDate),
            key: 'selection',
          },
        ]
      : [
          {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection',
          },
        ];
  //
  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className="calendarWrap">
        <TextField
          InputProps={{
            startAdornment: <InputAdornment position="start">{adornment}</InputAdornment>,
          }}
          label={label}
          value={dateValue}
          size="small"
          color="primary"
          onClick={() => setOpen(!open)}
          fullWidth
          disabled={disabled}
        />
        <div ref={refTarget}>
          {open && (
            <DateRange
              onChange={handleChange}
              editableDateInputs
              moveRangeOnFirstSelection={false}
              ranges={dateRange}
              months={1}
              direction="horizontal"
              className={cssClassName}
              maxDate={new Date(STAY_DATE_END_DATE)}
              minDate={addDays(new Date(), 1)}
              dateDisplayFormat="yyyy-MM-dd"
            />
          )}
        </div>
      </div>
    </ClickAwayListener>
  );
};
//
export default AlertDateRangePicker;
